import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { type CustomAdventure } from '@/utils/businessAppApi';
import { AdventureCard } from './AdventureCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Search, FilterX, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AdventureSelectionPanelProps {
  adventures: CustomAdventure[];
  selectedAdventure: CustomAdventure | null;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onAdventureSelect: (adventure: CustomAdventure) => void;
  onRefresh: () => void;
}

export const AdventureSelectionPanel = ({
  adventures,
  selectedAdventure,
  loading,
  error,
  searchQuery,
  setSearchQuery,
  onAdventureSelect,
  onRefresh
}: AdventureSelectionPanelProps) => {
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures' });

  const filteredAdventures = adventures.filter(adv => 
    adv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    adv.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    adv.options.some(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className={cn(
      "overflow-y-auto",
      selectedAdventure 
        ? "lg:col-span-4 hidden lg:block" 
        : "lg:col-span-12"
    )}>
      <div className="sticky top-0 bg-white z-10 pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Custom Adventures
        </h3>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10" />
          <Input
            placeholder="Search adventures..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 py-3 text-base bg-white shadow-sm rounded-lg border-gray-300 focus:ring-primary focus:border-primary"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0 h-8 w-8 rounded-full text-gray-500 hover:bg-gray-100"
              onClick={(e) => { e.preventDefault(); setSearchQuery(''); }}
              size="icon"
              aria-label="Clear search"
              type="button"
            >
              <FilterX className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="w-full">
                <CardContent className="p-0">
                  <Skeleton className="h-28 rounded-t-lg" />
                  <div className="p-3">
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('adventures.error.title')}</AlertTitle>
              <AlertDescription>
                {error}
                <Button
                  variant="link"
                  className="mt-2 text-destructive underline"
                  onClick={onRefresh}
                >
                  {t('adventures.error.action')}
                </Button>
              </AlertDescription>
            </Alert>
          </motion.div>
        ) : filteredAdventures.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-12"
          >
            <FilterX className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('adventures.empty.title')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('adventures.empty.description')}
            </p>
            <Button
              variant="outline"
              onClick={() => setSearchQuery('')}
            >
              {t('adventures.empty.action')}
            </Button>
          </motion.div>
        ) : (
          <motion.div 
            className={cn(
              "grid gap-3 lg:gap-4",
              selectedAdventure 
                ? "grid-cols-1 max-w-xs mx-auto lg:mx-0 lg:max-w-none" 
                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
            )}
            layout 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
          >
            {filteredAdventures.map((adventure) => (
              <motion.div
                key={adventure.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ type: 'spring', stiffness: 260, damping: 25 }}
              >
                <AdventureCard 
                  adventure={adventure}
                  isSelected={selectedAdventure?.id === adventure.id}
                  onClick={() => onAdventureSelect(adventure)}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
